import React, { useEffect, useState, useCallback } from "react";
import { Tabs, Select, TreeSelect, Typography, Popover, Tag, Modal, Button, Toast, Spin, Badge, Tooltip } from "@douyinfe/semi-ui";
import { IconChevronDown, IconTick, IconClose, IconInfoCircle } from "@douyinfe/semi-icons";
import { Col, ColType, Crud, CrudAction, CrudApi } from "../../comp/crud";
import { ApiClient, getLeadStatusOpt, getLeadTypeOpt, displayLeadStatus, displayLeadType, LeadAssignLog, LeadStarLog } from "../../api/ApiClient";
import { displayStar, formatRelativeTime, formatDateTime } from "../../utils";
import { TextCellRemark, TextCell } from "../../comp/cell";

const { Text } = Typography;
const apiClient = ApiClient.getDefault();

/** 状态列渲染（按状态分色 Tag） */
function StatusTag({ value }: { value: string }) {
  const colorMap: Record<string, string> = {
    PENDING_CONTACT: "red",
    UNREACHABLE: "blue",
    OUT_LEAD: "grey",
    INVITATION_IN_PROGRESS: "orange",
    NON_COMPLIANCE: "teal",
    PENDING_VISIT: "purple",
    VISITED: "green",
    SIGNED_CONTRACT: "violet",
    PAID: "yellow",
  };
  const color = colorMap[value] || "grey";
  return (
    <Tag color={color as any} size="small" shape="circle" type="solid">
      {displayLeadStatus(value)}
    </Tag>
  );
}

/** 分配/星级日志 Popover */
function LeadLogPopover({ leadId, children }: { leadId: number; children: React.ReactNode }) {
  const [assignLogs, setAssignLogs] = useState<LeadAssignLog[]>([]);
  const [starLogs, setStarLogs] = useState<LeadStarLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchData = useCallback(async () => {
    if (loaded) return;
    setLoading(true);
    try {
      const [assigns, stars] = await Promise.all([
        apiClient.getAllLeadAssignLogs(leadId),
        apiClient.getAllLeadStarLogs(leadId),
      ]);
      setAssignLogs(assigns);
      setStarLogs(stars);
      setLoaded(true);
    } catch (e) {
      console.error("加载日志失败:", e);
    } finally {
      setLoading(false);
    }
  }, [leadId, loaded]);

  const timelineItemStyle: React.CSSProperties = {
    position: "relative",
    paddingLeft: 20,
    paddingBottom: 12,
    borderLeft: "2px solid var(--semi-color-border)",
    marginLeft: 6,
  };

  const dotStyle: React.CSSProperties = {
    position: "absolute",
    left: -5,
    top: 2,
    width: 8,
    height: 8,
    borderRadius: "50%",
    border: "2px solid var(--semi-color-bg-0)",
  };

  const lastTimelineItemStyle: React.CSSProperties = {
    ...timelineItemStyle,
    paddingBottom: 0,
    borderLeft: "none",
  };

  const renderItem = (log: any, index: number, isLast: boolean, type: "assign" | "star") => (
    <div key={index} style={isLast ? lastTimelineItemStyle : timelineItemStyle}>
      <div style={{ ...dotStyle, background: type === "assign" ? "var(--semi-color-primary)" : "var(--semi-color-warning)" }} />
      <div style={{ background: "var(--semi-color-fill-0)", borderRadius: 6, padding: "8px 10px", marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
          {type === "assign" ? (
            <>
              <span style={{ padding: "1px 5px", background: "var(--semi-color-warning-light)", borderRadius: 3, fontSize: 11, fontWeight: 500 }}>{log.assignBy}</span>
              <span style={{ color: "var(--semi-color-text-2)", fontSize: 11 }}>→</span>
              <span style={{ padding: "1px 5px", background: "var(--semi-color-primary-light)", borderRadius: 3, fontSize: 11, fontWeight: 600 }}>{log.saleName}</span>
            </>
          ) : (
            <>
              <span style={{ fontWeight: 600, fontSize: 12, color: "var(--semi-color-warning)" }}>{displayStar(log.star)}</span>
              <Tag size="small" style={{ marginLeft: 2 }}>{displayLeadStatus(log.status)}</Tag>
            </>
          )}
          <span style={{ fontSize: 10, color: "var(--semi-color-text-2)", marginLeft: "auto" }}>{formatRelativeTime(log.createdAt)}</span>
        </div>
        {log.remark && (
          <div style={{ marginTop: 4, fontSize: 11, color: "var(--semi-color-text-1)", lineHeight: 1.4 }}>{log.remark}</div>
        )}
      </div>
    </div>
  );

  return (
    <Popover
      content={() => (
        <div style={{ width: 320, maxHeight: 360, overflow: "auto", padding: "8px 4px" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 20 }}><Spin /></div>
          ) : (
            <>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 12, color: "var(--semi-color-text-0)", display: "flex", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 3, height: 12, background: "var(--semi-color-primary)", borderRadius: 2 }} />
                  分配记录
                </div>
                {assignLogs.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 12, color: "var(--semi-color-text-2)", fontSize: 12 }}>暂无</div>
                ) : assignLogs.map((l, i) => renderItem(l, i, i === assignLogs.length - 1, "assign"))}
              </div>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 12, color: "var(--semi-color-text-0)", display: "flex", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 3, height: 12, background: "var(--semi-color-warning)", borderRadius: 2 }} />
                  星级状态
                </div>
                {starLogs.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 12, color: "var(--semi-color-text-2)", fontSize: 12 }}>暂无</div>
                ) : starLogs.map((l, i) => renderItem(l, i, i === starLogs.length - 1, "star"))}
              </div>
            </>
          )}
        </div>
      )}
      trigger="hover"
      onVisibleChange={(v) => { if (v) fetchData(); }}
    >
      {children}
    </Popover>
  );
}

/** 可分配销售单元格 - 人性化设计，支持搜索 */
function AssignableSaleCell({ rec, row, crudApi, openTodayList }: {
  rec: any;
  row: any;
  crudApi?: CrudApi;
  openTodayList: (saleId: number, saleName: string) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const candidates: any[] = row.candidateSales || [];

  // 搜索过滤
  const filteredCandidates = candidates.filter((c: any) => {
    if (!searchText) return true;
    const text = searchText.toLowerCase();
    return (
      c.saleName?.toLowerCase().includes(text) ||
      c.teamName?.toLowerCase().includes(text) ||
      String(c.saleId).includes(text)
    );
  });

  // 按团队分组
  const groupedCandidates = filteredCandidates.reduce((acc: any, c: any) => {
    const team = c.teamName || "其他";
    if (!acc[team]) acc[team] = [];
    acc[team].push(c);
    return acc;
  }, {});

  const handleAssign = async (targetSale: any) => {
    if (targetSale.overCap) {
      Toast.warning({ content: `${targetSale.saleName} 今日已达上限`, duration: 2 });
      return;
    }
    setLoading(true);
    setDropdownVisible(false);
    try {
      const res: any = await apiClient.request("/leadAssign/assignExplicit", "POST", undefined, {
        items: [{ leadId: row.id, saleId: targetSale.saleId }]
      });
      const fail = res?.data?.results?.find((r: any) => !r.success);
      if (fail) {
        Toast.error({ content: fail.msg || "分配失败", duration: 3 });
      } else {
        Toast.success({ content: `已分配给 ${targetSale.saleName}`, duration: 2 });
        crudApi?.refresh();
      }
    } catch (e: any) {
      Toast.error({ content: e.message || "分配失败", duration: 3 });
    } finally {
      setLoading(false);
    }
  };

  const formatSaleLabel = (sale: any) => {
    if (sale.teamName) {
      return `${sale.saleName}（${sale.teamName}）`;
    }
    return sale.saleName;
  };

  if (!rec) {
    return (
      <Tooltip content="所有可分配销售均被排除（历史跟进人或当前销售）">
        <Text type="danger" size="small" style={{ cursor: "help" }}>
          <IconInfoCircle style={{ marginRight: 4 }} />无可用
        </Text>
      </Tooltip>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {/* 推荐销售按钮 - 点击直接分配 */}
      <Button
        theme="light"
        type="warning"
        size="small"
        loading={loading}
        onClick={() => {
          Modal.confirm({
            title: "确认分配",
            content: (
              <div style={{ lineHeight: 1.8 }}>
                <div>👤 客户：<b>{row.name}</b></div>
                <div>分配给：<b>{formatSaleLabel(rec)}</b></div>
                <div style={{ marginTop: 8, padding: "6px 8px", background: "var(--semi-color-fill-0)", borderRadius: 4, fontSize: 12 }}>
                  <div>今日已分配：<b>{rec.todayCount}</b> 个</div>
                  {rec.overCap && <div style={{ color: "var(--semi-color-danger)", marginTop: 2 }}>⚠️ 该销售今日已达上限</div>}
                </div>
              </div>
            ),
            onOk: () => handleAssign(rec),
          });
        }}
        style={{ fontWeight: 600, minWidth: 100 }}
      >
        {formatSaleLabel(rec)}
      </Button>

      {/* 今日数徽章 */}
      <Badge count={rec.todayCount} overflowCount={99} type={rec.overCap ? "danger" : "warning"}>
        <Button
          theme="borderless"
          size="small"
          type="warning"
          onClick={() => openTodayList(rec.saleId, rec.saleName)}
          style={{ padding: "0 4px", fontSize: 11 }}
        >
          今日
        </Button>
      </Badge>

      {/* 选择其他销售 - 带搜索的下拉 */}
      {candidates.length > 1 && (
        <Popover
          trigger="click"
          visible={dropdownVisible}
          onVisibleChange={(v) => {
            setDropdownVisible(v);
            if (!v) setSearchText("");
          }}
          content={() => (
            <div style={{ width: 320, maxHeight: 400, display: "flex", flexDirection: "column" }}>
              {/* 搜索框 */}
              <div style={{ padding: "8px 12px", borderBottom: "1px solid var(--semi-color-border)" }}>
                <input
                  autoFocus
                  placeholder="搜索销售姓名或团队..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    border: "1px solid var(--semi-color-border)",
                    borderRadius: 4,
                    fontSize: 12,
                    outline: "none",
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                />
                <div style={{ marginTop: 4, fontSize: 10, color: "var(--semi-color-text-2)" }}>
                  共 {candidates.length} 个可选销售，{filteredCandidates.length} 个匹配
                </div>
              </div>

              {/* 候选列表 - 按团队分组 */}
              <div style={{ overflow: "auto", maxHeight: 320, padding: "4px 0" }}>
                {Object.entries(groupedCandidates).length === 0 ? (
                  <div style={{ padding: 16, textAlign: "center", color: "var(--semi-color-text-2)", fontSize: 12 }}>
                    未找到匹配的销售
                  </div>
                ) : (
                  Object.entries(groupedCandidates).map(([team, teamCandidates]: [string, any]) => (
                    <div key={team}>
                      {/* 团队标题 */}
                      <div style={{
                        padding: "4px 12px",
                        fontSize: 10,
                        fontWeight: 600,
                        color: "var(--semi-color-text-2)",
                        background: "var(--semi-color-fill-0)",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}>
                        {team}
                      </div>
                      {/* 团队成员 */}
                      {teamCandidates.map((c: any) => (
                        <div
                          key={c.saleId}
                          onClick={() => handleAssign(c)}
                          style={{
                            padding: "8px 12px",
                            cursor: c.overCap ? "not-allowed" : "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            opacity: c.overCap ? 0.6 : 1,
                            background: c.saleId === rec.saleId ? "var(--semi-color-primary-light-hover)" : undefined,
                            borderBottom: "1px solid var(--semi-color-border)",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) => {
                            if (!c.overCap) {
                              e.currentTarget.style.background = "var(--semi-color-fill-1)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = c.saleId === rec.saleId ? "var(--semi-color-primary-light-hover)" : "";
                          }}
                        >
                          {/* 头像占位 */}
                          <div style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            background: c.saleId === rec.saleId ? "var(--semi-color-primary)" : "var(--semi-color-fill-2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: c.saleId === rec.saleId ? "#fff" : "var(--semi-color-text-1)",
                            fontSize: 11,
                            fontWeight: 600,
                            flexShrink: 0,
                          }}>
                            {c.saleName?.charAt(0) || "?"}
                          </div>

                          {/* 信息区 */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontWeight: c.saleId === rec.saleId ? 600 : 500,
                              fontSize: 13,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}>
                              {c.saleName}
                              {c.saleId === rec.saleId && (
                                <span style={{ fontSize: 10, color: "var(--semi-color-primary)", marginLeft: 4 }}>推荐</span>
                              )}
                            </div>
                            <div style={{ fontSize: 11, color: "var(--semi-color-text-2)", marginTop: 1 }}>
                              {c.teamName || "未知团队"}
                            </div>
                          </div>

                          {/* 今日数 */}
                          <div style={{
                            padding: "2px 6px",
                            borderRadius: 4,
                            background: c.overCap ? "var(--semi-color-danger-light-default)" : "var(--semi-color-fill-0)",
                            color: c.overCap ? "var(--semi-color-danger)" : "var(--semi-color-text-1)",
                            fontSize: 11,
                            fontWeight: 500,
                            flexShrink: 0,
                          }}>
                            今日 {c.todayCount}
                          </div>

                          {/* 超限标记 */}
                          {c.overCap && (
                            <div style={{
                              padding: "2px 4px",
                              borderRadius: 4,
                              background: "var(--semi-color-danger)",
                              color: "#fff",
                              fontSize: 10,
                              fontWeight: 600,
                              flexShrink: 0,
                            }}>
                              已满
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        >
          <div
            style={{
              padding: "4px 6px",
              cursor: "pointer",
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              gap: 2,
              color: "var(--semi-color-text-2)",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--semi-color-fill-1)";
              e.currentTarget.style.color = "var(--semi-color-text-0)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "";
              e.currentTarget.style.color = "var(--semi-color-text-2)";
            }}
          >
            <IconChevronDown size="small" />
            <span style={{ fontSize: 11 }}>更多</span>
          </div>
        </Popover>
      )}
    </div>
  );
}

export default function LeadAssign() {
  const [tab, setTab] = useState<string>("team");
  const [teamId, setTeamId] = useState<number | undefined>(undefined);
  const [crudApi, setCrudApi] = useState<CrudApi | undefined>(undefined);
  const [teamOpts, setTeamOpts] = useState<any[]>([]);

  useEffect(() => { apiClient.getTeamAsOpts().then(setTeamOpts); }, []);

  // Today modal
  const [todayModal, setTodayModal] = useState<{ visible: boolean; saleId?: number; saleName?: string }>({ visible: false });
  const openTodayList = (saleId: number, saleName: string) => setTodayModal({ visible: true, saleId, saleName });

  // Batch modal
  const [batchVisible, setBatchVisible] = useState(false);
  const [batchRows, setBatchRows] = useState<any[]>([]);
  const [batchMap, setBatchMap] = useState<Record<number, any>>({});
  const [batchLoading, setBatchLoading] = useState(false);

  const openBatch = (_a: any, rows: any[]) => {
    const selected = rows || [];
    if (!selected.length) {
      Toast.warning({ content: "请先勾选客户", duration: 2 });
      return;
    }
    const m: Record<number, any> = {};
    selected.forEach((r: any) => { m[r.id] = r.recommendedSale || (r.candidateSales?.[0]); });
    setBatchRows(selected);
    setBatchMap(m);
    setBatchVisible(true);
  };

  const handleBatchAssign = async () => {
    const items = Object.entries(batchMap)
      .filter(([, c]) => c && c.saleId)
      .map(([leadId, c]) => ({ leadId: Number(leadId), saleId: (c as any).saleId }));
    if (!items.length) {
      Toast.warning({ content: "请为至少一个客户选择销售", duration: 2 });
      return;
    }

    setBatchLoading(true);
    try {
      const res: any = await apiClient.request("/leadAssign/assignExplicit", "POST", undefined, { items });
      const results = res?.data?.results || [];
      const successCount = results.filter((r: any) => r.success).length;
      const fails = results.filter((r: any) => !r.success);

      setBatchVisible(false);
      crudApi?.refresh();

      if (fails.length === 0) {
        Toast.success({ content: `成功分配 ${successCount} 个客户`, duration: 3 });
      } else {
        Modal.warning({
          title: "部分分配失败",
          content: (
            <div>
              <div style={{ marginBottom: 8 }}>成功 {successCount} 个，失败 {fails.length} 个</div>
              {fails.map((f: any, i: number) => (
                <div key={i} style={{ fontSize: 12, color: "var(--semi-color-text-1)" }}>
                  • 客户 {f.leadId}: {f.msg}
                </div>
              ))}
            </div>
          ),
        });
      }
    } catch (e: any) {
      Toast.error({ content: e.message || "批量分配失败", duration: 3 });
    } finally {
      setBatchLoading(false);
    }
  };

  // todayCols
  const todayCols: Col[] = [
    { name: "姓名", key: "name", type: ColType.STRING, width: 80 },
    { name: "客户类型", key: "leadType", type: ColType.STRING, render: (v: string) => displayLeadType(v) },
    { name: "城市", key: "applyCity", type: ColType.STRING, width: 60 },
    { name: "星级", key: "star", type: ColType.NUMBER, render: (v: number) => displayStar(v), width: 60 },
    { name: "状态", key: "status", type: ColType.STRING, render: (v: string) => <StatusTag value={v} /> },
    { name: "分配时间", key: "assignedAt", type: ColType.DATETIME, render: (v: string) => formatDateTime(v) },
  ];

  // Main columns - 优化后的列顺序
  const cols: Col[] = [
    // === 客户信息列（第一列） ===
    {
      name: "客户", key: "name", type: ColType.STRING, showInFilter: true, width: 80,
      render: (v: any, row: any) => (
        <div>
          <Text
            link
            strong
            onClick={() => window.open("/#/leadDetailNew?data=" + encodeURIComponent(JSON.stringify({ id: row.id })), "_blank")}
          >
            {v}
          </Text>
          <div style={{ fontSize: 11, color: "var(--semi-color-text-2)", marginTop: 2 }}>
            {displayLeadType(row.leadType)}
          </div>
        </div>
      ),
    },
    // === 核心操作列 ===
    {
      name: "推荐分配", key: "recommendedSale", type: ColType.STRING, width: 180,
      render: (rec: any, row: any) => (
        <AssignableSaleCell rec={rec} row={row} crudApi={crudApi} openTodayList={openTodayList} />
      ),
    },
    
    {
      name: "历史跟进人", key: "historicalFollowers", type: ColType.STRING, width: 80,
      render: (v: any[]) => {
        const list = (v || []).slice().sort((a: any, b: any) => (a.latestAssignAt || "").localeCompare(b.latestAssignAt || ""));
        if (!list.length) return <Text type="tertiary" size="small">-</Text>;
        return (
          <Popover
            content={() => (
              <div style={{ width: 260 }}>
                <div style={{ padding: "6px 8px", fontSize: 11, color: "var(--semi-color-text-2)", borderBottom: "1px solid var(--semi-color-border)", fontWeight: 600 }}>
                  近3月跟进记录（{list.length}人）
                </div>
                {list.map((f: any) => (
                  <div key={f.saleId} style={{ padding: "8px", borderBottom: "1px solid var(--semi-color-border)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <Text strong>{f.saleName}</Text>
                      <Text size="small" type="secondary">{formatRelativeTime(f.latestAssignAt)}</Text>
                    </div>
                    {f.lastContactAt && (
                      <div style={{ fontSize: 11, color: "var(--semi-color-text-1)" }}>
                        最后跟进：{f.lastContactAt}
                      </div>
                    )}
                    {f.lastContactContent && (
                      <div style={{ fontSize: 11, color: "var(--semi-color-text-2)", marginTop: 2 }}>
                        {f.lastContactContent}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            position="top"
          >
            <Tag  color="orange" style={{ cursor: "pointer" }}>
              {list.length}人
            </Tag>
          </Popover>
        );
      },
    },
    { name: "分配时间", key: "assignedAt", type: ColType.DATE_RANGE, showInFilter: true, hideInList: false, width: 150 },
    {
      name: "状态", key: "status", type: ColType.SELECT, showInFilter: true, width: 80,
      getOptions: () => Promise.resolve(getLeadStatusOpt()),
      render: (v: string, row: any) => (
        <LeadLogPopover leadId={row.id}>
          <StatusTag value={v} />
        </LeadLogPopover>
      ),
    },
    {
      name: "星级", key: "star", type: ColType.NUMBER, width: 70,
      render: (v: number, row: any) => (
        <LeadLogPopover leadId={row.id}>
          <span style={{ cursor: "pointer" }}>{displayStar(v)}</span>
        </LeadLogPopover>
      ),
    },
    {
          name: "资质",
          key: "qualification",
          colSort: 7,
          type: ColType.STRING,
          width: 210,
          hideInCreate: true,
          showInFilter: true,
          render: (a, b, c) => <TextCell text={a} width={200} />,
    },
    // === 跟进信息列 ===
    {
      name: "跟进记录", key: "contactLogs", type: ColType.STRING, width: 200,
      render: (v: any) => <TextCellRemark moreText={v ? v : []} width={200} />,
    },
    // === 筛选专用列（隐藏在列表） ===
    { name: "客户ID", key: "id", type: ColType.STRING, showInFilter: true, hideInList: true },
    { name: "手机", key: "mobile", type: ColType.STRING, showInFilter: true, hideInList: true },
    { name: "城市", key: "applyCity", type: ColType.SELECT, showInFilter: true, hideInList: true,
      getOptions: () => apiClient.getDictAsOpts("city") },
    { name: "客户类型", key: "leadType", type: ColType.SELECT, showInFilter: true, hideInList: true,
      getOptions: () => Promise.resolve(getLeadTypeOpt()) },
    { name: "星级筛选", key: "stars", type: ColType.MULTI_SELECT, showInFilter: true, hideInList: true,
      getOptions: () => Promise.resolve([0, 1, 2, 3, 4, 5].map(n => ({ value: String(n), label: n + "星", key: String(n) }))) },
   
    // === 其他展示列 ===
    { name: "当前销售", key: "saleName", type: ColType.STRING, width: 80 },
    {
      name: "今日分配", key: "todayAssignedSales", type: ColType.STRING, width: 100,
      render: (v: any[]) => {
        const list = v || [];
        if (!list.length) return <Text type="tertiary" size="small">-</Text>;
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {list.slice(0, 2).map((s: any) => (
              <Tag key={s.saleId} color="green" style={{ margin: 0 }}>{s.saleName}</Tag>
            ))}
            {list.length > 2 && (
              <Tooltip content={list.slice(2).map((s: any) => s.saleName).join("、")}>
                <Tag color="green" style={{ margin: 0 }}>+{list.length - 2}</Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  const params: Record<string, any> = { tab };
  if (tab === "pool") params.teamId = teamId;
  const canLoad = tab !== "pool" || !!teamId;

  return (
    <div style={{ padding: "5px 5px", background: "var(--semi-color-bg-0)", minHeight: "100%" }}>
      {/* Header */}
      <div style={{ marginBottom: 5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Tabs activeKey={tab} onChange={(k) => setTab(k as string)} size="small">
          <Tabs.TabPane tab="团队客户" itemKey="team" />
          <Tabs.TabPane tab="公共池客户" itemKey="pool" />
        </Tabs>
        {tab === "pool" && (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 12, color: "var(--semi-color-text-2)" }}>选择部门：</span>
              <TreeSelect
                style={{ width: 240, borderColor: !teamId ? "var(--semi-color-primary)" : undefined }}
                size="small"
                treeData={teamOpts}
                value={teamId}
                onChange={(v) => setTeamId(v as number)}
                placeholder="👇 请先选择部门"
                filterTreeNode
                searchPlaceholder="搜索部门..."
              />
            </div>
            <Tooltip content="选择部门后，系统将从该部门及下属部门中筛选可分配的销售作为候选">
              <IconInfoCircle style={{ color: "var(--semi-color-text-2)", cursor: "help" }} />
            </Tooltip>
          </div>
        )}
      </div>

      {/* Main Table or Empty State */}
      {tab === "pool" && !teamId ? (
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "80px 20px",
          background: "var(--semi-color-bg-1)",
          borderRadius: 8,
          border: "2px dashed var(--semi-color-border)",
        }}>
          <div style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "var(--semi-color-primary-light-default)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 16,
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="var(--semi-color-primary)"/>
            </svg>
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "var(--semi-color-text-0)", marginBottom: 8 }}>
            请先选择部门
          </div>
          <div style={{ fontSize: 13, color: "var(--semi-color-text-2)", textAlign: "center", lineHeight: 1.6, maxWidth: 500 }}>
            选择部门后，系统将从该部门及下属部门中筛选可分配的销售作为候选
          </div>
          <div style={{
            marginTop: 24,
            padding: "12px 20px",
            background: "var(--semi-color-info-light-default)",
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}>
            <IconInfoCircle style={{ color: "var(--semi-color-info)" }} />
            <span style={{ fontSize: 12, color: "var(--semi-color-text-1)" }}>
              点击上方「👇 请先选择部门」下拉框选择部门
            </span>
          </div>
        </div>
      ) : canLoad && (
        <Crud
          key={tab + (teamId ?? "")}
          name={tab === "team" ? "团队客户-分配" : "公共池客户-分配"}
          entityKey="leadAssign/page"
          pageSize={50}
          params={params}
          select
          rowActionWidth={0}
          canCreate={false}
          canUpdate={false}
          canExport={false}
          cols={cols}
          getCrudApi={(api) => setCrudApi(api)}
          actions={[
            { name: "批量分配", action: openBatch } as CrudAction,
          ]}
        />
      )}

      {/* Today Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span>今日已分配客户</span>
            <Tag size="small" color="blue">{todayModal.saleName}</Tag>
          </div>
        }
        visible={todayModal.visible}
        footer={null}
        onCancel={() => setTodayModal({ visible: false })}
        width={800}
        style={{ top: 40 }}
      >
        {todayModal.visible && (
          <Crud
            name=""
            entityKey="leadAssign/todayAssignedList"
            pageSize={20}
            params={{ tab, teamId, saleId: todayModal.saleId }}
            canCreate={false}
            canUpdate={false}
            canExport={false}
            cols={todayCols}
            hideTitle
          />
        )}
      </Modal>

      {/* Batch Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>批量分配客户</span>
            <Button
              theme="borderless"
              size="small"
              onClick={() => {
                // 全部使用推荐销售
                const m: Record<number, any> = {};
                batchRows.forEach((r: any) => {
                  m[r.id] = r.recommendedSale || (r.candidateSales?.[0]);
                });
                setBatchMap(m);
              }}
            >
              全部推荐
            </Button>
          </div>
        }
        visible={batchVisible}
        width={560}
        onCancel={() => setBatchVisible(false)}
        onOk={handleBatchAssign}
        okButtonProps={{ loading: batchLoading }}
        okText={`确认分配 (${Object.values(batchMap).filter(Boolean).length}/${batchRows.length})`}
        style={{ top: 60 }}
      >
        <div style={{ maxHeight: 420, overflow: "auto" }}>
          {batchRows.map((row, index) => {
            const c = batchMap[row.id];
            const candidates = row?.candidateSales || [];
            return (
              <div
                key={row.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "8px 12px",
                  background: index % 2 === 0 ? "var(--semi-color-fill-0)" : undefined,
                  borderRadius: 4,
                  marginBottom: 4,
                }}
              >
                <div style={{ width: 120, flexShrink: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{row.name}</div>
                  <div style={{ fontSize: 11, color: "var(--semi-color-text-2)" }}>
                    {displayLeadType(row.leadType)} · {row.applyCity || "-"}
                  </div>
                </div>
                <Select
                  style={{ flex: 1 }}
                  filter
                  value={c?.saleId}
                  optionList={candidates.map((s: any) => ({
                    value: s.saleId,
                    label: `${s.saleName} - ${s.teamName} - ${s.roleName} (今日${s.todayCount}${s.overCap ? " 已满" : ""})`,
                    disabled: s.overCap,
                  }))}
                  onChange={(v) => setBatchMap({ ...batchMap, [row.id]: candidates.find((s: any) => s.saleId === v) })}
                />
              </div>
            );
          })}
        </div>
        {batchRows.length > 0 && (
          <div style={{ marginTop: 12, padding: "8px 12px", background: "var(--semi-color-fill-0)", borderRadius: 4, fontSize: 12, color: "var(--semi-color-text-2)" }}>
            提示：灰色选项表示该销售今日已达到分配上限
          </div>
        )}
      </Modal>
    </div>
  );
}
